import { createSlice } from '@reduxjs/toolkit';
import { validateModel } from '../validation/reviewDetails';
import { validateForReview as validateAD, updateAddressDetails } from './addressDetails';
import { validateForReview as validatePD, updatePersonalDetails } from './personalDetails';
import { validateForReview as validateCD, updateContactDetails } from './contactDetails';
import { validateForReview as validateED, updateEmploymentDetails } from './employmentDetails';
import { validateForReview as validateFD, updateFinancialDetails } from './financialDetails';
import { finishEditing } from './api';

const initialState = {
  validation: [],
  hasTriedToSave: false,
  isDirty: false,
  isSaving: false,
  isLoading: false,
  canContinue: false,
  existingData: {
    started: false,
    loaded: false
  },
  meta: {
    addressDetails: {
      title: 'Address details',
      current: 'Current address',
      previous: 'Previous address history',
      fields: {
        address: 'Address',
        previousAddress: 'Previous address',
        residentialStatus: 'Residential status',
        dateMoved: 'Date moved',
        historyIncomplete: 'Address details'
      }
    },
    contactDetails: {
      title: 'Contact details',
      fields: {
        emailAddress: 'Email address',
        mobileNumber: 'Mobile number',
        homePhoneNumber: 'Home phone number',
        workPhoneNumber: 'Work phone number',
        preferredMethod: 'Preferred contact method'
      }
    },
    employmentDetails: {
      title: 'Employment details',
      fields: {
        employmentStatus: 'Employment status',
        employerName: `Employer's name`,
        employmentStartDate: 'When you started work there'
      }
    },
    financialDetails: {
      title: 'Income and expenses',
      income: 'Income',
      expenses: 'Expenses',
      fields: {
        grossAnnualIncome: 'Annual income before tax',
        netMonthlyIncome: 'Monthly income after tax',
        mortgageExpense: 'Mortgage or rent'
      }
    },
    personalDetails: {
      title: 'Personal details',
      citizenship: 'Citizenship',
      fields: {
        title: 'Title',
        firstName: 'First name',
        middleName: 'Middle name',
        lastName: 'Last name',
        birthDate: 'Date of birth',
        gender: 'Gender',
        maritalStatus: 'Marital status',
        numberOfDependants: 'Number of dependents',
        UKCitizenship: 'UK Citizen',
        otherCountries: `Other countries you're a citizen of`,
        otherCountry: `Other countries you're a citizen of`
      }
    }
  },
  model: {
    confirmDetails: false
  },
  requiresNewDIP: false
};

const reviewDetails = createSlice({
  name: 'reviewDetails',
  initialState,
  reducers: {
    loadReviewDetails: (state) => {
      state.isLoading = true;
    },
    loadReviewDetailsCompleted: (state, action) => {
      state.model = action.payload;
      state.isLoading = false;
    },
    loadReviewDetailsRejected: (state) => {
      state.isLoading = false;
    },
    modelChange: (state, { payload: { section, data } }) => {
      state.model[section] = data;
      state.isDirty = true;
    },
    fieldChange: (state, { payload: { field, value, section } }) => {
      if (section) {
        state.model[section][field] = value;
      } else {
        state.model[field] = value;
      }
      state.isDirty = true;
    },
    validate: (state, action) => {
      if (action.payload) {
        state.validation = validateModel(action.payload);
      }
      state.canContinue = state.validation.length === 0 && state.model.confirmDetails;
    },
    resetValidation: (state) => {
      state.validation = initialState.validation;
      state.canContinue = false;
    },
    save: (state) => {
      state.isSaving = true;
      state.hasTriedToSave = true;
    },
    saveCompleted: (state) => {
      state.isSaving = false;
    },
    saveCancelled: (state) => {
      state.isSaving = false;
    },
    saveRejected: (state) => {
      state.isSaving = false;
    },
    setConfirmStatus: (state, action) => {
      state.model.confirmDetails = action.payload;
    },
    resetState: () => initialState,
    updateExistingCustomerStarted: (state) => {
      state.existingData.started = true;
    },
    updateExistingCustomerCompleted: (state) => {
      state.existingData.loaded = true;
    },
    updateRequiresNewDIP: (state, action) => {
      state.requiresNewDIP = action.payload;
    }
  }
});

export function updateExistingCustomerData(payload) {
  return async (dispatch) => {
    dispatch(updateExistingCustomerStarted());
    await dispatch(updatePersonalDetails(payload));
    await dispatch(updateContactDetails(payload));
    await dispatch(updateEmploymentDetails(payload));
    await dispatch(updateFinancialDetails(payload));
    await dispatch(updateAddressDetails(payload));
    dispatch(updateExistingCustomerCompleted());
  };
}

export function validateReviewScreen(payload) {
  return async (dispatch) => {
    await dispatch(validateAD(payload.addressDetails, payload.personalDetails));
    const {
      title,
      firstName,
      middleName,
      lastName,
      sex: gender,
      ...personalDetails
    } = payload.personalDetails;
    await dispatch(
      validatePD(
        {
          name: {
            title,
            firstName,
            middleName,
            lastName
          },
          gender,
          ...personalDetails
        },
        true
      )
    );
    await dispatch(validateCD(payload.contactDetails));
    await dispatch(validateED(payload.employmentDetails));
    await dispatch(validateFD(payload.financialDetails));
    await dispatch(validateReview());
  };
}

export function validateReview() {
  return async (dispatch, getState) => {
    await dispatch(validate(getState()));
  };
}

export function tryToContinue(model, callback) {
  return async (dispatch) => {
    await dispatch(validateReviewScreen(model));
    dispatch(checkCanContinue(callback));
  };
}

function checkCanContinue(callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().reviewDetails;
    if (canContinue) {
      dispatch(resetValidation());
      dispatch(finishEditing());
      if (typeof callback === 'function') {
        callback();
      }
    }
  };
}

export { reviewDetails, validateModel };
export const {
  fieldChange,
  loadReviewDetails,
  loadReviewDetailsCompleted,
  loadReviewDetailsRejected,
  modelChange,
  save,
  saveCancelled,
  saveCompleted,
  saveRejected,
  setConfirmStatus,
  validate,
  resetValidation,
  resetState,
  updateExistingCustomerStarted,
  updateExistingCustomerCompleted,
  updateRequiresNewDIP
} = reviewDetails.actions;
